import { useEffect, useRef, useState, type FormEvent, type KeyboardEvent } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useSession } from "../../shell/session";
import { durationLabel, formatDateTime, formatTime, utcToAucklandInput } from "../../../shared/time";
import type { PlanDetail } from "../../../shared/types";
import { Badge, Dialog, ErrorNote } from "../../ui";
import { archivePlan, duplicatePlan, loadPlan, restorePlan, updatePlan } from "./client";
import { PlanCreatePage } from "./create";
import { NotesPanel } from "./notes";
import { OrderBoard } from "./order";
import { TeamBoard } from "./team";
import { canLead, countPending, countSongs, localInputToUtc, messageOf, shiftLocalInput, stageColor, standHref, statusTone } from "./model";
import "./services.css";

type Tab = "order" | "team" | "notes";

export function PlanPage() {
  const { planId = "" } = useParams();
  if (planId === "new") return <PlanCreatePage />;
  return <PlanWorkspace planId={planId} />;
}

function PlanWorkspace({ planId }: { planId: string }) {
  const { user } = useSession();
  const [search, setSearch] = useSearchParams();
  const requested = search.get("tab");
  const tab: Tab = requested === "team" || requested === "notes" ? requested : "order";
  const [plan, setPlan] = useState<PlanDetail | null>(null);
  const [error, setError] = useState("");
  const [duplicating, setDuplicating] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const tabRefs = useRef<Partial<Record<Tab, HTMLButtonElement | null>>>({});
  const leader = canLead(user?.role);
  const canEdit = leader && plan?.status !== "archived";

  useEffect(() => {
    let cancel = false;
    setPlan(null);
    setError("");
    void loadPlan(planId)
      .then((row) => { if (!cancel) setPlan(row); })
      .catch((caught: unknown) => { if (!cancel) setError(messageOf(caught)); });
    return () => { cancel = true; };
  }, [planId]);

  function selectTab(next: Tab) {
    const params = new URLSearchParams(search);
    if (next === "order") params.delete("tab");
    else params.set("tab", next);
    setSearch(params, { replace: true });
  }

  function onTabsKey(event: KeyboardEvent<HTMLDivElement>) {
    const order: Tab[] = ["order", "team", "notes"];
    const index = order.indexOf(tab);
    const nextIndex = event.key === "ArrowRight" ? (index + 1) % order.length : event.key === "ArrowLeft" ? (index - 1 + order.length) % order.length : -1;
    const next = order[nextIndex];
    if (!next) return;
    event.preventDefault();
    selectTab(next);
    tabRefs.current[next]?.focus();
  }

  if (!plan && error) {
    return (
      <section className="page svc">
        <ErrorNote>{error}</ErrorNote>
        <Link className="btn" to="/services">All plans</Link>
      </section>
    );
  }
  if (!plan) return <section className="page svc">Loading the plan…</section>;

  const pending = countPending(plan);

  return (
    <section className="page svc">
      <header className="panel panel-pad svc-hero" style={{ borderTopColor: stageColor(plan.serviceTypeColor) }}>
        <div className="page-head">
          <div>
            <p className="kicker">{plan.serviceTypeName}</p>
            <h1 className="page-title">{plan.title}</h1>
            <p className="lede">{formatDateTime(plan.startsAt)} to {formatTime(plan.endsAt)}. {countSongs(plan)} songs in {plan.items.length} rows · {durationLabel(plan.totalSeconds)}.</p>
            {plan.rehearsals[0] && <p className="muted">Rehearsal {formatDateTime(plan.rehearsals[0].startsAt)}{plan.rehearsals[0].place ? ` · ${plan.rehearsals[0].place}` : ""}{plan.rehearsals.length > 1 ? ` · ${plan.rehearsals.length} calls` : ""}</p>}
            {countSongs(plan) === 0 && <p className="faint">Add a song before you expect a chart on the stand.</p>}
          </div>
          <div className="cluster">
            <Link className="btn ghost" to="/services">All plans</Link>
            <Link className="btn teal" to={standHref(plan.id)}>Open Music Stand</Link>
            {leader && plan.status !== "archived" && (
              <button className="btn small" type="button" onClick={() => void updatePlan(plan.id, { status: plan.status === "confirmed" ? "draft" : "confirmed" }).then(setPlan).catch((caught: unknown) => setError(messageOf(caught)))}>
                {plan.status === "confirmed" ? "Mark draft" : "Confirm plan"}
              </button>
            )}
            {leader && <button className="btn small" type="button" onClick={() => setDuplicating(true)}>Duplicate</button>}
            {leader && plan.status !== "archived" && <button className="btn small danger" type="button" onClick={() => setArchiving(true)}>Archive</button>}
            {leader && plan.status === "archived" && <button className="btn small" type="button" onClick={() => void restorePlan(plan.id).then(setPlan).catch((caught: unknown) => setError(messageOf(caught)))}>Restore to draft</button>}
          </div>
        </div>
        <div className="cluster">
          <Badge tone={statusTone(plan.status)}>{plan.status}</Badge>
          {plan.series && <Badge>{plan.series}</Badge>}
          {pending > 0 && <button className="btn small" type="button" onClick={() => selectTab("team")}>{pending} responses waiting</button>}
        </div>
        {plan.status === "archived" && <p className="notice">This plan is archived. Restore it to draft before changing the order or the team.</p>}
        {error && <ErrorNote>{error}</ErrorNote>}
      </header>
      <div className="svc-tabs" role="tablist" aria-label="Plan" onKeyDown={onTabsKey}>
        {([["order", "Order"], ["team", "Team"], ["notes", "Notes"]] as const).map(([id, label]) => (
          <button
            key={id}
            ref={(node) => { tabRefs.current[id] = node; }}
            className="svc-chip"
            type="button"
            role="tab"
            id={`plan-tab-${id}`}
            aria-selected={tab === id}
            aria-controls={`plan-panel-${id}`}
            tabIndex={tab === id ? 0 : -1}
            onClick={() => selectTab(id)}
          >{label}</button>
        ))}
      </div>
      <div role="tabpanel" id={`plan-panel-${tab}`} aria-labelledby={`plan-tab-${tab}`}>
        {tab === "order" && <OrderBoard plan={plan} canEdit={canEdit} onPlan={setPlan} />}
        {tab === "team" && <TeamBoard plan={plan} canEdit={canEdit} personId={user?.personId ?? null} onPlan={setPlan} />}
        {tab === "notes" && <NotesPanel plan={plan} canEdit={canEdit} onPlan={setPlan} />}
      </div>
      {duplicating && <DuplicateDialog plan={plan} onClose={() => setDuplicating(false)} />}
      {archiving && <ArchiveDialog plan={plan} onClose={() => setArchiving(false)} onPlan={setPlan} />}
    </section>
  );
}

function DuplicateDialog({ plan, onClose }: { plan: PlanDetail; onClose: () => void }) {
  const navigate = useNavigate();
  const [title, setTitle] = useState(`${plan.title.slice(0, 195)} copy`);
  const [when, setWhen] = useState(shiftLocalInput(utcToAucklandInput(plan.startsAt), 7));
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const startsAt = localInputToUtc(when);
    if (!title.trim()) { setError("Give the copy a title."); return; }
    if (!startsAt) { setError("Enter a valid Auckland date and time."); return; }
    setBusy(true);
    setError("");
    try {
      const copy = await duplicatePlan(plan.id, { title: title.trim(), startsAt });
      navigate(`/services/${copy.id}`);
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Duplicate plan" onClose={onClose}>
      <form className="form-grid" onSubmit={(event) => void submit(event)}>
        {error && <ErrorNote>{error}</ErrorNote>}
        <p className="muted">Songs, keys, teams, and rehearsals are copied. People are asked again. Earlier acceptances are not kept, and no email is sent.</p>
        <div className="field"><label htmlFor="copy-title">Title</label><input id="copy-title" value={title} maxLength={200} onChange={(event) => setTitle(event.target.value)} /></div>
        <div className="field"><label htmlFor="copy-when">Starts (Auckland)</label><input id="copy-when" type="datetime-local" value={when} onChange={(event) => setWhen(event.target.value)} /></div>
        <button className="btn primary" type="submit" disabled={busy}>{busy ? "Copying…" : "Create copy"}</button>
      </form>
    </Dialog>
  );
}

function ArchiveDialog({ plan, onClose, onPlan }: { plan: PlanDetail; onClose: () => void; onPlan: (plan: PlanDetail) => void }) {
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  async function archive() {
    setBusy(true);
    setError("");
    try {
      onPlan(await archivePlan(plan.id));
      onClose();
    } catch (caught) {
      setError(messageOf(caught));
      setBusy(false);
    }
  }

  return (
    <Dialog title="Archive plan" onClose={onClose}>
      <div className="stack">
        {error && <ErrorNote>{error}</ErrorNote>}
        <p>Archive {plan.title}? It leaves the active board. Restore brings it back as a draft. The Music Stand link still opens it.</p>
        <div className="cluster">
          <button className="btn danger" type="button" disabled={busy} onClick={() => void archive()}>{busy ? "Archiving…" : "Archive"}</button>
          <button className="btn ghost" type="button" onClick={onClose}>Keep it active</button>
        </div>
      </div>
    </Dialog>
  );
}
